import cloneDeep from 'lodash/cloneDeep';
import config from '@plone/volto/registry';
import { deepEqual } from '@designsystem/stories/01-core/Utilities';

import { blocksConfig } from './blocksConfig';
import {
  defaultModuleGroups,
  LEGACY_MODULE_GROUP_ID,
} from '../../components/Blocks/Modules/groups';

// Blocks not rendered at all
export const skipBlocks = [
  'title',
  'description',
  'introduction',
  'toc',
  'edubox',
  'eventMetadata',
  'eventdetail',
  'newsdetail',
  'leadimage',
  'highlight',
  'separator',
  'eventapplication',
  'applicationAccordion',
  'newsdetail',
  '__button',
];

// Seeded block titles from before the rename to "Programmaufbau" (#1717)
const RENAMED_MODULES_TITLES: Record<string, string> = {
  'Weiterführende Programme': 'Programmaufbau',
  'Continuative programmes': 'Programme structure',
};

// Data transformers for individual block types
const blockTransformers: Record<
  string,
  (data: any, result: any, root?: any) => any
> = {
  // Transform existing data into new media data format
  imageslider: (data: any, result: any) => {
    data = { ...data };

    if (data?.slides) {
      data.slides = data.slides.map((slide: any) => {
        if (slide?.media_type) {
          return slide;
        }

        return {
          '@id': slide?.['@id'],
          media_type: 'image',
          image: slide?.href?.[0],
          image_title: slide?.title,
          image_caption: slide?.description,
        };
      });
    }
    return data;
  },
  infoevent: (data: any, result: any) => {
    if (!('block_title' in data)) {
      data.block_title =
        result?.language?.token === 'en'
          ? 'Info-Events'
          : result?.language?.token === 'fr'
            ? 'Info-Events'
            : 'Info-Anlässe';
      data.block_compact = true;
    }
    return data;
  },
  newsletter: (data: any, result: any) => {
    if (!('block_title' in data) && data?.title) {
      data.block_title = data.title;
    }
    if (!('block_description' in data) && data?.description) {
      data.block_description = data.description;
    }
    return data;
  },
  teaser: (data: any, result: any) => {
    if (!('actions' in data) && data?.buttonText) {
      data.actions = [
        {
          '@id': 'button',
          type: 'primary',
          url: data?.href?.[0]?.['@id'],
          label: data.buttonText,
        },
      ];
    }

    return data;
  },
  testimonial: (data: any, result: any) => {
    if (!('testimonials' in data) && data?.testimonial) {
      data.testimonials = [
        {
          '@id': 'testimonial',
          description: data.testimonial,
          name: data.name,
          image: data?.image?.[0],
        },
      ];
    }

    return data;
  },
  eventapplication: (data: any, result: any) => {
    data['@type'] = 'application';
    return data;
  },
  applicationAccordion: (data: any, result: any) => {
    data['@type'] = 'application';
    return data;
  },
  slate: (data: any, result: any) => {
    if (data?.value && !Array.isArray(data.value)) {
      data.value = [data.value];
    }
    return data;
  },
  __button: (data: any, result: any) => {
    data['@type'] = 'buttons';

    if (data?.items && data.items.length > 0) {
      return data;
    }

    if (data?.title) {
      data.items = [
        {
          '@id': 'button',
          type: data?.styles?.variation === 'black' ? 'primary' : 'secondary',
          url: data?.href?.[0]?.['@id'],
          label: data.title,
          new_tab: data?.openLinkInNewTab || false,
        },
      ];
    }

    return data;
  },
  // Park block-maintained modules in a neutral group #1717
  modules: (data: any, result: any, root: any = result) => {
    if ('moduleGroups' in data) {
      return data;
    }

    const hasLegacyModules =
      Array.isArray(data?.modules) && data.modules.length > 0;
    // A block with its own entries showed only those
    data.moduleGroups = hasLegacyModules
      ? [
          ...defaultModuleGroups(false),
          {
            '@id': LEGACY_MODULE_GROUP_ID,
            showGroup: true,
            modules: [...data.modules],
          },
        ]
      : defaultModuleGroups();

    // The view no longer falls back to the page's product name — freeze the
    // value existing pages showed so far
    if (!data.degree && root?.product_name) {
      data.degree = root.product_name;
    }

    const renamedTitle = RENAMED_MODULES_TITLES[data.title];
    if (renamedTitle) {
      data.title = renamedTitle;
    }

    return data;
  },
  image: (data: any, result: any) => {
    if (
      data?.size === 'l' &&
      data?.format in ['large', 'half', 'third', 'portrait', 'original']
    ) {
      data.size = data.format;
      delete data.format;
    }
    return data;
  },
};

const collectAndTransformBlockTypes = (block: any, root: any): string[] => {
  const types: string[] = [];

  if (Array.isArray(block)) {
    for (const b of block) {
      types.push(...collectAndTransformBlockTypes(b, root));
    }
    return types;
  } else if (typeof block !== 'object' || block === null) {
    return types;
  }

  if (block?.['@type']) {
    types.push(block['@type']);
  }

  // Transform empty lists into paragraphs to prevent errors in the editor #1233
  if (
    (block?.type === 'ol' || block?.type === 'ul') &&
    !block?.children?.length
  ) {
    block.type = 'p';
    block.children = [{ text: '' }];
  }

  if (block?.blocks && block?.blocks_layout) {
    block = transformBlocks(block, false, root);
    const keys = Object.keys(block.blocks);
    for (const i in keys) {
      types.push(...collectAndTransformBlockTypes(block.blocks[keys[i]], root));
    }
    return types;
  }

  const keys = Object.keys(block);
  for (const i in keys) {
    types.push(...collectAndTransformBlockTypes(block[keys[i]], root));
  }

  return types;
};

/**
 * Blocks transformer
 *
 * - Removes all blocks to be skipped and adds required blocks per content type
 *   if needed.
 * - Migrates block data on the fly for mutated block configs
 *
 * @function transformBlocks
 * @param {Object} result Content action result.
 * @param {Object} mark Whether to mark the result as transformed.
 * @returns {Object} Transformed result.
 */
export const transformBlocks = (
  result: any = {},
  mark: boolean = true,
  root: any = result,
) => {
  if (typeof result !== 'object') {
    return result;
  }

  if (!result?.blocks) {
    result.blocks = [];
  }
  if (!result?.blocks_layout) {
    result.blocks_layout = { items: [] };
  }

  const original = {
    blocks: cloneDeep(result.blocks),
    blocks_layout: cloneDeep(result.blocks_layout),
  };

  const seen = [];
  const keys = Object.keys(result.blocks);
  if (keys.length > 0) {
    for (const i in keys) {
      if (result.blocks[keys[i]]?.['@type'] in blockTransformers) {
        result.blocks[keys[i]] = blockTransformers[
          result.blocks[keys[i]]['@type'] as keyof typeof blockTransformers
        ](result.blocks[keys[i]], result, root);
      }

      if (
        skipBlocks.includes(result.blocks[keys[i]]?.['@type']) ||
        !result.blocks_layout.items.includes(keys[i])
      ) {
        delete result.blocks[keys[i]];
      } else {
        seen.push(
          ...collectAndTransformBlockTypes(result.blocks[keys[i]], root),
        );
      }
    }
    result.blocks_layout.items = result.blocks_layout.items.filter(
      (key: string) => key in result.blocks,
    );
  }

  if (result?.['@type'] in blocksConfig) {
    blocksConfig[result['@type'] as keyof typeof blocksConfig](result, seen);
  }

  if (!mark) {
    return result;
  }

  if (
    !deepEqual(original.blocks_layout.items, result.blocks_layout.items) ||
    !deepEqual(original.blocks, result.blocks)
  ) {
    result.blocks_transformed = true;
  } else if (result?.blocks_transformed) {
    delete result.blocks_transformed;
  }

  return result;
};

const registerTransformer = () => {
  config.registerUtility({
    name: 'blocks',
    type: 'transform',
    dependencies: { reducer: 'content' },
    method: transformBlocks,
  });
};

export default registerTransformer;
